<script lang="ts" setup>
// Shared "Create my copy" confirm for the subscriber customize/fork flow. Both the Workflows tab
// and the Builder open this before forking a base workflow, so the copy + wording stay in one place.
// The parent owns the actual customize call (via the shared useBuilderNavigation composable) — this
// component is presentation + confirm/cancel only.

interface Props {
  show: boolean;
  workflowName?: string;
  pending?: boolean;
  errorMessage?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "confirm"): void;
  (e: "cancel"): void;
}>();

function onBackdrop() {
  if (props.pending) return;
  emit("cancel");
}
</script>

<template>
  <div
    v-if="props.show"
    class="modal fade show d-block"
    tabindex="-1"
    @click="onBackdrop"
  >
    <div class="modal-dialog" @click.stop>
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title mb-0">Customize workflow</h2>
          <button
            type="button"
            class="btn-close"
            :disabled="props.pending"
            @click="emit('cancel')"
          ></button>
        </div>
        <div class="modal-body">
          <p>
            This will create your own customizable copy of
            <strong>{{ props.workflowName }}</strong>.
          </p>
          <ul class="small text-muted">
            <li>Your fork is owned by this project — your edits won't affect the publisher's master.</li>
            <li>Future updates the publisher makes to this workflow won't apply to your fork until you accept a product upgrade (which discards customizations).</li>
            <li>You can keep editing your fork via the Builder once it's created.</li>
          </ul>
          <div v-if="props.errorMessage" class="alert alert-danger mt-3 mb-0">
            <i class="bi bi-exclamation-circle me-2"></i>
            Failed to customize.
            <span> {{ props.errorMessage }}</span>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-secondary-cta"
            :disabled="props.pending"
            @click="emit('cancel')"
          >Cancel</button>
          <button
            type="button"
            class="bb-btn-primary-cta"
            :disabled="props.pending"
            @click="emit('confirm')"
          >
            <span
              v-if="props.pending"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            <i v-else class="bi bi-wrench me-1"></i>
            Create my copy
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
